import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Tooltip } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useAppStore } from '../../stores';
import { CheckIconOutlined } from '../../../custom_shared/icons';
import { BusinessResponseDto } from '../../types';
import './ContractProcessingResultsRenderer.less';

const ContractProcessingResultsRenderer: React.FC = () => {
    const { contractSubmissionStore } = useAppStore();

    if (!contractSubmissionStore.contractProcessingResults) {
        return null;
    }

    const renderScenarioLabel = (business: BusinessResponseDto): string => {
        switch (business.scenario) {
            case 'create':
                return 'Created';
            case 'update':
                return 'Updated';
            case 'renew':
                return 'Renewed';
            default:
                return '';
        }
    };

    const renderHeader = () => {
        if (!contractSubmissionStore.businesses.length) {
            return null;
        }

        if (contractSubmissionStore.businesses.length === 1) {
            return (
                <span>
                    The contract was successfully{' '}
                    {renderScenarioLabel(contractSubmissionStore.businesses[0]).toLowerCase()}
                </span>
            );
        }

        return <span>Contract(s) submitted successfully.</span>;
    };

    const renderSingleBusiness = (business: BusinessResponseDto) => {
        const businessId = business.businessId;

        if (!businessId) {
            return null;
        }

        return (
            <div className="single-item-container">
                <span className="key-label">Business ID:</span>
                <span>{businessId}</span>
                <Tooltip title="Copy">
                    <CopyOutlined className="copy-icon" onClick={() => navigator.clipboard.writeText(businessId)} />
                </Tooltip>
            </div>
        );
    };

    const renderMultipleBusinesses = (businesses: BusinessResponseDto[]) => (
        <div className="multiple-items-container">
            <span className="key-label">Business results:</span>
            <ul>
                {businesses.map((business, index) => {
                    const businessId = business.businessId;

                    if (!businessId) {
                        return null;
                    }

                    return (
                        <li key={`${businessId}-${index}`}>
                            <span>
                                {businessId} - {renderScenarioLabel(business)}
                                <Tooltip title="Copy">
                                    <CopyOutlined
                                        className="copy-icon"
                                        onClick={() => navigator.clipboard.writeText(businessId)}
                                    />
                                </Tooltip>
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );

    const renderBusinessResults = () => {
        if (!contractSubmissionStore.businesses.length) {
            return null;
        }

        if (contractSubmissionStore.businesses.length === 1) {
            return renderSingleBusiness(contractSubmissionStore.businesses[0]);
        }

        return renderMultipleBusinesses(contractSubmissionStore.businesses);
    };

    return (
        <div className="contract-processing-results-renderer-container">
            <div className="results-content">
                <div className="results-left-column">
                    <CheckIconOutlined />
                </div>

                <div className="results-right-column">
                    {renderHeader()}

                    {contractSubmissionStore.contractTitle && (
                        <div className="single-item-container">
                            <span className="key-label">Contract title:</span>
                            <span>{contractSubmissionStore.contractTitle}</span>
                        </div>
                    )}

                    {contractSubmissionStore.programId && (
                        <div className="single-item-container">
                            <span className="key-label">Program ID:</span>
                            <span>{contractSubmissionStore.programId}</span>
                        </div>
                    )}

                    {renderBusinessResults()}
                </div>
            </div>
        </div>
    );
};

export default observer(ContractProcessingResultsRenderer);
